import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { count as offlineQueueCount, replayQueue } from '../../utils/offlineBookingQueue';
import '../../styles/OfflineIndicator.css';

/**
 * OfflineIndicator - Network connectivity status banner
 *
 * Features:
 * - Detects online/offline status
 * - Slide-in animation when offline
 * - Auto-hide when back online
 * - Mobile-safe-area aware
 * - Retry button for manual check
 * - Offline booking queue: shows how many bookings are waiting to be submitted
 *   while offline, and on reconnect replays them ONCE (api.bookRoom), then
 *   surfaces an honest summary (some queued bookings may fail if the slot was
 *   taken in the meantime — bookaroom is a real-time booking system).
 */
function OfflineIndicator() {
  const { t } = useTranslation();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [showReconnected, setShowReconnected] = useState(false);
  // How many bookings are queued locally awaiting submission.
  const [pendingCount, setPendingCount] = useState(() => offlineQueueCount());
  // Brief human summary of the last replay, e.g. "2 submitted, 1 could not be
  // booked". Cleared after a short delay. null = nothing to show.
  const [replaySummary, setReplaySummary] = useState(null);

  // Re-entrancy guard for replay at the component level. The queue module also
  // guards internally, but this prevents us from even kicking off overlapping
  // replays when 'online' fires repeatedly (real event + our favicon poll).
  const replayingRef = useRef(false);
  // Track timeouts so we can clear them on unmount (avoid setState-after-unmount).
  const timeoutsRef = useRef([]);

  const scheduleTimeout = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  // Replay the offline booking queue exactly once per reconnect. Updates the
  // pending count and surfaces an honest summary. Safe to call repeatedly: the
  // ref guard + the queue's own in-flight guard make extra calls no-ops.
  const runReplay = useCallback(async () => {
    if (replayingRef.current) return;
    // Nothing queued — keep the count fresh and bail without spamming a replay.
    if (offlineQueueCount() === 0) {
      setPendingCount(0);
      return;
    }
    replayingRef.current = true;
    try {
      const summary = await replayQueue(api.bookRoom);
      // A skipped result means another replay was already running; don't report.
      if (!summary.skipped && (summary.submitted > 0 || summary.failed > 0)) {
        if (summary.failed > 0) {
          setReplaySummary(
            t('offline.queueReplaySummaryWithFailures', {
              submitted: summary.submitted,
              failed: summary.failed,
            })
          );
        } else {
          setReplaySummary(
            t('offline.queueReplaySummary', { submitted: summary.submitted })
          );
        }
        // Clear the summary after a few seconds so it doesn't linger forever.
        scheduleTimeout(() => setReplaySummary(null), 6000);
      }
    } catch (err) {
      // replayQueue is defensive and shouldn't throw, but never let the banner
      // break the app if it does.
      console.error('Offline queue replay failed:', err);
    } finally {
      replayingRef.current = false;
      setPendingCount(offlineQueueCount());
    }
  }, [t, scheduleTimeout]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);

      // Show "reconnected" message briefly if we were offline
      if (wasOffline) {
        setShowReconnected(true);
        scheduleTimeout(() => {
          setShowReconnected(false);
          setWasOffline(false);
        }, 3000);
      }

      // Replay any queued bookings now that we're back online. Guarded so
      // repeated 'online' events (e.g. our favicon poll) can't double-submit.
      runReplay();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      setShowReconnected(false);
      // Refresh the pending count when we drop offline.
      setPendingCount(offlineQueueCount());
    };

    // Keep the pending count in sync if another tab queues/replays a booking.
    const handleStorage = () => {
      setPendingCount(offlineQueueCount());
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('storage', handleStorage);

    // Periodic connectivity check (every 30 seconds when offline)
    let checkInterval;
    if (!isOnline) {
      checkInterval = setInterval(() => {
        fetch('/favicon.ico', {
          method: 'HEAD',
          cache: 'no-cache'
        })
          .then(() => {
            if (!navigator.onLine) {
              window.dispatchEvent(new Event('online'));
            }
          })
          .catch(() => {
            // Still offline
          });
      }, 30000);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('storage', handleStorage);
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [isOnline, wasOffline, runReplay, scheduleTimeout]);

  // On mount: if we're already online and have bookings left over from a
  // previous session (queued offline, then the tab was closed/reopened online),
  // no 'online' event will fire — so kick off one guarded replay here. Runs once.
  useEffect(() => {
    if (navigator.onLine && offlineQueueCount() > 0) {
      runReplay();
    }
    // Intentionally mount-only: subsequent reconnects are handled by the
    // 'online' listener above. runReplay is stable (useCallback).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While online and idle, keep the pending count reasonably fresh so a booking
  // queued from BookingForm (network drop mid-request) shows up promptly.
  useEffect(() => {
    if (!isOnline) return undefined;
    const id = setInterval(() => setPendingCount(offlineQueueCount()), 5000);
    return () => clearInterval(id);
  }, [isOnline]);

  // Clear any pending timeouts on unmount.
  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, []);

  // Manual retry
  const handleRetry = () => {
    fetch('/favicon.ico', {
      method: 'HEAD',
      cache: 'no-cache'
    })
      .then(() => {
        window.dispatchEvent(new Event('online'));
      })
      .catch(() => {
        console.log('Still offline');
      });
  };

  const hasPending = pendingCount > 0;

  // Don't render anything if we're online, haven't been offline, have nothing
  // queued, and have no replay summary to show.
  if (isOnline && !showReconnected && !hasPending && !replaySummary) {
    return null;
  }

  return (
    <>
      {/* Offline Banner */}
      {!isOnline && (
        <div className="offline-indicator offline" role="alert" aria-live="assertive">
          <div className="offline-content">
            <span className="offline-icon" aria-hidden="true">📡</span>
            <div className="offline-text">
              <strong className="offline-title">
                {t('offline.title')}
              </strong>
              <span className="offline-message">
                {t('offline.message')}
              </span>
              {hasPending && (
                <span className="offline-message offline-queue-count">
                  {t('offline.queuePending', { count: pendingCount })}
                </span>
              )}
            </div>
            <button
              type="button"
              className="offline-retry-btn"
              onClick={handleRetry}
              aria-label={t('offline.retry')}
            >
              <span className="retry-icon" aria-hidden="true">🔄</span>
            </button>
          </div>
        </div>
      )}

      {/* Reconnected Banner */}
      {showReconnected && (
        <div className="offline-indicator online" role="status" aria-live="polite">
          <div className="offline-content">
            <span className="offline-icon" aria-hidden="true">✓</span>
            <div className="offline-text">
              <strong className="offline-title">
                {t('offline.reconnected')}
              </strong>
              <span className="offline-message">
                {replaySummary || t('offline.reconnectedMessage')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Online + idle, but bookings are still queued (e.g. a network drop
          mid-request queued one while navigator.onLine stayed true, or a replay
          retained items). Surface the count and a summary so the user is never
          misled into thinking a queued booking is confirmed. */}
      {isOnline && !showReconnected && (hasPending || replaySummary) && (
        <div
          className="offline-indicator online"
          role="status"
          aria-live="polite"
          /* Cancel the .online fadeOut so this stays visible while bookings are
             still queued (CSS is out of scope for this change). */
          style={{ animation: 'slideDown 0.3s ease-out' }}
        >
          <div className="offline-content">
            <span className="offline-icon" aria-hidden="true">⏳</span>
            <div className="offline-text">
              <strong className="offline-title">
                {replaySummary || t('offline.queuePendingTitle')}
              </strong>
              {hasPending && (
                <span className="offline-message">
                  {t('offline.queuePending', { count: pendingCount })}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OfflineIndicator;
